import React, { useState, useEffect } from 'react';
import { Trophy, RotateCcw, Lightbulb, Star } from 'lucide-react';
import { playSound } from '../../utils/soundManager';

const WordScramble = ({ onComplete, difficulty = 'easy' }) => {
  const [currentWord, setCurrentWord] = useState(null);
  const [scrambledWord, setScrambledWord] = useState('');
  const [userAnswer, setUserAnswer] = useState('');
  const [score, setScore] = useState(0);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [gameWon, setGameWon] = useState(false);

  // Word lists based on difficulty
  const wordLists = {
    easy: [
      { word: 'CAT', hint: 'A small furry pet that meows', category: 'Animal' },
      { word: 'DOG', hint: 'A loyal pet that barks', category: 'Animal' },
      { word: 'SUN', hint: 'Bright star in the sky', category: 'Nature' },
      { word: 'TREE', hint: 'Tall plant with leaves', category: 'Nature' },
      { word: 'BOOK', hint: 'You read this', category: 'Object' },
      { word: 'FISH', hint: 'Swims in water', category: 'Animal' },
      { word: 'BIRD', hint: 'Has wings and flies', category: 'Animal' },
      { word: 'MOON', hint: 'Shines at night', category: 'Nature' },
      { word: 'STAR', hint: 'Twinkles in the sky', category: 'Nature' },
      { word: 'BALL', hint: 'Round toy to play with', category: 'Object' }
    ],
    medium: [
      { word: 'TIGER', hint: 'National animal of Bangladesh', category: 'Animal' },
      { word: 'DHAKA', hint: 'Capital of Bangladesh', category: 'Place' },
      { word: 'RIVER', hint: 'Water flows in this', category: 'Nature' },
      { word: 'MANGO', hint: 'Sweet yellow fruit', category: 'Food' },
      { word: 'FLOWER', hint: 'Beautiful and colorful plant', category: 'Nature' },
      { word: 'SCHOOL', hint: 'Where you learn', category: 'Place' },
      { word: 'CRICKET', hint: 'Popular sport in Bangladesh', category: 'Sport' },
      { word: 'HILSA', hint: 'Famous fish of Bangladesh', category: 'Food' },
      { word: 'RAINBOW', hint: 'Colorful arc in the sky', category: 'Nature' },
      { word: 'GARDEN', hint: 'Place with flowers and plants', category: 'Place' }
    ],
    hard: [
      { word: 'SUNDARBANS', hint: 'Largest mangrove forest', category: 'Place' },
      { word: 'JACKFRUIT', hint: 'National fruit of Bangladesh', category: 'Food' },
      { word: 'BUTTERFLY', hint: 'Colorful flying insect', category: 'Animal' },
      { word: 'ELEPHANT', hint: 'Large animal with trunk', category: 'Animal' },
      { word: 'MOUNTAIN', hint: 'Very tall landform', category: 'Nature' },
      { word: 'COMPUTER', hint: 'Electronic device for work', category: 'Technology' },
      { word: 'KABADDI', hint: 'National sport of Bangladesh', category: 'Sport' },
      { word: 'RICKSHAW', hint: 'Common transport in Bangladesh', category: 'Transport' },
      { word: 'BENGALI', hint: 'National language of Bangladesh', category: 'Language' },
      { word: 'INDEPENDENCE', hint: 'Freedom from control', category: 'History' }
    ]
  };

  const totalQuestions = 5;

  useEffect(() => {
    loadNewWord();
  }, [difficulty]);

  const scrambleWord = (word) => {
    const arr = word.split('');
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr.join('');
  };

  const loadNewWord = () => {
    const words = wordLists[difficulty];
    const randomWord = words[Math.floor(Math.random() * words.length)];
    setCurrentWord(randomWord);
    
    let scrambled = scrambleWord(randomWord.word);
    // Make sure scrambled is different from original
    while (scrambled === randomWord.word && randomWord.word.length > 3) {
      scrambled = scrambleWord(randomWord.word);
    }
    
    setScrambledWord(scrambled);
    setUserAnswer('');
    setShowHint(false);
    setFeedback('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (userAnswer.toUpperCase() === currentWord.word) {
      playSound('correct');
      setFeedback('✅ Correct!');
      setScore(score + 1);
      
      setTimeout(() => {
        const newQuestionsAnswered = questionsAnswered + 1;
        setQuestionsAnswered(newQuestionsAnswered);
        
        if (newQuestionsAnswered >= totalQuestions) {
          setGameWon(true);
          if (onComplete) {
            onComplete({ 
              completed: true,
              score: score + 1, // 1 XP per correct answer
              total: totalQuestions,
              percentage: Math.round(((score + 1) / totalQuestions) * 100)
            });
          }
        } else {
          loadNewWord();
        }
      }, 1000);
    } else {
      playSound('incorrect');
      setFeedback('❌ Try again!');
      setTimeout(() => setFeedback(''), 1500);
    }
  };

  const handleSkip = () => {
    playSound('click');
    const newQuestionsAnswered = questionsAnswered + 1;
    setQuestionsAnswered(newQuestionsAnswered);
    
    if (newQuestionsAnswered >= totalQuestions) {
      setGameWon(true);
      if (onComplete) {
        onComplete({ 
          completed: true,
          score, // 1 XP per correct answer
          total: totalQuestions,
          percentage: Math.round((score / totalQuestions) * 100)
        });
      }
    } else {
      loadNewWord();
    }
  };

  const handleReset = () => {
    playSound('click');
    setScore(0);
    setQuestionsAnswered(0);
    setGameWon(false);
    loadNewWord();
  };

  const toggleHint = () => {
    setShowHint(!showHint);
    playSound('click');
  };

  if (!currentWord) return null;

  return (
    <div className="word-scramble">
      <div className="game-header">
        <div className="game-stats">
          <div className="stat-item">
            <span className="stat-label">Score:</span>
            <span className="stat-value">{score}/{totalQuestions}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Question:</span>
            <span className="stat-value">{questionsAnswered + 1}/{totalQuestions}</span>
          </div>
        </div>
        
        <button 
          className="reset-button hover-lift"
          onClick={handleReset}
        >
          <RotateCcw size={20} />
          Reset
        </button>
      </div>

      {!gameWon ? (
        <div className="game-content">
          <div className="word-category">
            <span className="category-badge">{currentWord.category}</span>
          </div>

          <div className="scrambled-word-display">
            {scrambledWord.split('').map((letter, index) => (
              <div key={index} className="letter-box animate-scaleIn">
                {letter}
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="answer-form">
            <input
              type="text"
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value.toUpperCase())}
              placeholder="Type your answer..."
              className="answer-input"
              autoFocus
            />
            
            <div className="button-group">
              <button type="submit" className="submit-button hover-lift">
                Check Answer
              </button>
              <button 
                type="button" 
                className="hint-button hover-lift"
                onClick={toggleHint}
              >
                <Lightbulb size={18} />
                {showHint ? 'Hide' : 'Show'} Hint
              </button>
              <button 
                type="button" 
                className="skip-button hover-lift"
                onClick={handleSkip}
              >
                Skip
              </button>
            </div>
          </form>

          {showHint && (
            <div className="hint-box animate-fadeIn">
              <Lightbulb size={20} />
              <span>{currentWord.hint}</span>
            </div>
          )}

          {feedback && (
            <div className={`feedback ${feedback.includes('✅') ? 'correct' : 'incorrect'}`}>
              {feedback}
            </div>
          )}
        </div>
      ) : (
        <div className="game-won-overlay">
          <div className="game-won-modal animate-scaleIn">
            <Trophy size={60} color="#f39c12" />
            <h2>🎉 Game Complete!</h2>
            <p>You scored {score} out of {totalQuestions}!</p>
            <div className="win-stats">
              <div className="win-stat">
                <Star size={20} color="#f39c12" />
                <span>Accuracy: {Math.round((score / totalQuestions) * 100)}%</span>
              </div>
            </div>
            <button 
              className="play-again-button hover-lift"
              onClick={handleReset}
            >
              Play Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WordScramble;
